import { useState } from "react";
import { Check, Copy, TicketPercent } from "lucide-react";
import type { CampaignOffer } from "@/lib/api";
import { formatDay, offerLive } from "@/lib/campaign";
import { Button } from "@/components/ui/button";

/**
 * A campaign page's offer: a line, a sentence, and optionally a discount code
 * to copy. It hides itself after its last day, so an ad left running past the
 * offer doesn't promise one. Checkout decides whether the code applies.
 */
export function OfferBanner({ offer }: { offer: CampaignOffer }) {
  const [copied, setCopied] = useState(false);
  if (!offerLive(offer)) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(offer.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard (an old browser, an insecure context): the code is on
      // screen to type.
    }
  }

  return (
    <section className="mx-auto max-w-7xl px-5 pt-12 sm:px-8">
      <div className="flex flex-col gap-5 rounded-3xl bg-brand-light/60 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div className="flex items-start gap-4">
          <TicketPercent className="mt-1 size-6 shrink-0 text-brand" />
          <div>
            <h2 className="text-2xl font-medium tracking-tight text-ink">{offer.title}</h2>
            {offer.body && <p className="mt-1 text-ink/70">{offer.body}</p>}
            {offer.endsOn && <p className="mt-2 text-sm text-ink/55">Berlaku sampai {formatDay(offer.endsOn)}</p>}
          </div>
        </div>
        {offer.code && (
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="rounded-xl border border-dashed border-brand/50 bg-white px-4 py-2 font-mono text-lg font-semibold tracking-wider text-brand-dark">
              {offer.code}
            </span>
            <Button variant="outline" size="sm" onClick={() => void copy()} aria-label={`Salin kode ${offer.code}`}>
              {copied ? <Check /> : <Copy />}
              {copied ? "Tersalin" : "Salin"}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
